import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, History, Pencil, Ruler, Trash2 } from 'lucide-react';
import type { MeasurementProfile } from '@tailoring-crm/shared';
import { formatDate, formatRelativeTime } from '../../lib/format.js';
import {
  useDeleteProfile,
  useOwnerProfiles,
  type MeasurementOwner,
} from '../../lib/measurements/use-profiles.js';
import { useTemplates } from '../../lib/measurements/use-templates.js';
import { cn, Badge, Button, useConfirm, ListSkeleton, useToast } from '@tailoring-crm/ui';
import { MeasurementCaptureDialog } from './measurement-capture-dialog.js';
import { SketchPicture } from './sketch-picture.js';
import { ProfileHistoryDialog } from './profile-history-dialog.js';

/**
 * Saved measurements, on the customer hub.
 *
 * It is the strongest reason a customer comes back, so the list leads with WHOSE measurements
 * each set is and WHEN they were taken — a profile two years old is shown as two years old,
 * plainly, because measuring again is cheap and stitching to a stale set is not.
 */

/** Older than this and the shop is nudged to re-measure. Roughly eighteen months. */
const STALE_AFTER_MS = 18 * 30 * 24 * 60 * 60 * 1000;

export interface MeasurementsPanelProps {
  owner: MeasurementOwner;
  /**
   * A frozen record. No capture, no edit, no delete — but History stays, because "what did we
   * have before?" is exactly the question a record is kept to answer.
   */
  readOnly?: boolean;
  className?: string;
}

export function MeasurementsPanel({
  owner,
  readOnly = false,
  className,
}: MeasurementsPanelProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();

  const profiles = useOwnerProfiles(owner);
  const templates = useTemplates();
  const remove = useDeleteProfile();

  const [capturing, setCapturing] = useState<string | null>(null);
  const [editing, setEditing] = useState<MeasurementProfile | null>(null);
  const [duplicating, setDuplicating] = useState<MeasurementProfile | null>(null);
  const [viewingHistory, setViewingHistory] = useState<MeasurementProfile | null>(null);

  const onDelete = async (profile: MeasurementProfile): Promise<void> => {
    const confirmed = await confirm({
      title: t('profiles.deleteTitle', { name: profile.name }),
      description: t('profiles.deleteDescription'),
      confirmLabel: t('common.delete'),
      destructive: true,
    });
    if (!confirmed) return;
    await remove.mutateAsync(profile.id);
    toast({ title: t('profiles.deleted', { name: profile.name }), variant: 'success' });
  };

  const rows = profiles.data ?? [];

  const allTemplates = templates.data?.templates ?? [];

  return (
    <section className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Ruler aria-hidden="true" className="size-4" />
          {t('profiles.heading')}
        </h3>
        {/* The garment picker IS the add button: choosing what you are measuring and starting
            to measure are one action at a counter. */}
        {readOnly ? null : (
          <select
            aria-label={t('profiles.chooseGarment')}
            data-tour="measure-add"
            value=""
            onChange={(event) => event.target.value && setCapturing(event.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">{t('profiles.add')}</option>
            {allTemplates.map((template) => (
              <option key={template.garmentTypeKey} value={template.garmentTypeKey}>
                {t('profiles.addFor', { garment: template.garmentTypeLabel })}
              </option>
            ))}
          </select>
        )}
      </div>

      {profiles.isPending ? (
        <ListSkeleton rows={2} />
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          {`${t('profiles.empty')} ${readOnly ? '' : t('profiles.emptyHint')}`.trim()}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((profile) => {
            const stale = Date.now() - new Date(profile.takenAt).getTime() > STALE_AFTER_MS;
            return (
              <li
                key={profile.id}
                className="flex flex-col gap-2 rounded-lg border border-border p-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      {profile.name}
                      <Badge variant="secondary">{profile.garmentTypeLabel}</Badge>
                      {stale ? (
                        <Badge variant="warning">
                          {t('profiles.staleWarning', {
                            age: formatRelativeTime(profile.takenAt).replace(/^/, ''),
                          })}
                        </Badge>
                      ) : null}
                    </p>
                    {/* The date, always. A profile stale by two years must look stale. */}
                    <p className="text-sm text-muted-foreground">
                      {t('profiles.takenOn', { date: formatDate(profile.takenAt) })}
                      {profile.takenByName
                        ? ` · ${t('profiles.takenBy', { name: profile.takenByName })}`
                        : ''}
                      {` · ${t('profiles.valueCount', { count: profile.values.length })}`}
                    </p>
                  </div>

                  <div className="flex shrink-0 gap-1">
                    {readOnly ? null : (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('profiles.edit')}
                          onClick={() => setEditing(profile)}
                        >
                          <Pencil aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('profiles.duplicate')}
                          onClick={() => setDuplicating(profile)}
                        >
                          <Copy aria-hidden="true" />
                        </Button>
                      </>
                    )}
                    {/* Kept even when read-only: a frozen record exists to be read back. */}
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('profiles.history')}
                      onClick={() => setViewingHistory(profile)}
                    >
                      <History aria-hidden="true" />
                    </Button>
                    {readOnly ? null : (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('profiles.delete')}
                        onClick={() => void onDelete(profile)}
                      >
                        <Trash2 aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>

                {profile.notes ? (
                  <p className="text-sm text-muted-foreground">{profile.notes}</p>
                ) : null}

                {profile.sketch ? (
                  <SketchPicture
                    previewMediaId={profile.sketch.previewMediaId}
                    name={profile.name}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {/* Closing is the dialog's decision, not this component's: a save that produced warnings
          stays open so the shop can read them. Unmounting it here on `onSaved` would throw
          those warnings away in the same tick they were raised. */}
      {capturing ? (
        <MeasurementCaptureDialog
          open
          onOpenChange={(open) => !open && setCapturing(null)}
          owner={owner}
          garmentTypeKey={capturing}
        />
      ) : null}

      {editing ? (
        <MeasurementCaptureDialog
          open
          onOpenChange={(open) => !open && setEditing(null)}
          owner={owner}
          garmentTypeKey={editing.garmentTypeKey}
          profile={editing}
        />
      ) : null}

      {duplicating ? (
        <MeasurementCaptureDialog
          open
          onOpenChange={(open) => !open && setDuplicating(null)}
          owner={owner}
          garmentTypeKey={duplicating.garmentTypeKey}
          duplicateOf={duplicating}
        />
      ) : null}

      {viewingHistory ? (
        <ProfileHistoryDialog
          profileId={viewingHistory.id}
          profileName={viewingHistory.name}
          onClose={() => setViewingHistory(null)}
        />
      ) : null}
    </section>
  );
}
